import { useState } from "react";
import { Link } from "react-router-dom";

/** A single toggleable question/answer pair — shared by the Info page FAQ
 * and any LARP-Wissen article FAQ (dedicated `qa` page or `embeddedFaq`
 * section). `answer` may contain "\n\n"-separated paragraphs. Optional
 * `links` (`{ label, href }[]`) render as real links below the answer —
 * for the rare item that needs a clickable reference, since the answer
 * text itself stays plain text. An `href` starting with "/" is treated as
 * an internal route (client-side <Link>); anything else opens externally. */
export function FaqItem({ question, answer, links }) {
  const [open, setOpen] = useState(false);

  return (
    <div style={{ background: "var(--color-bg-raised)", borderRadius: "var(--radius-card)", padding: "24px 26px" }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "24px",
          width: "100%",
          background: "none",
          border: "none",
          padding: 0,
          margin: 0,
          font: "inherit",
          textAlign: "left",
          cursor: "pointer",
          color: "var(--color-cream)",
        }}
      >
        {/* min-width: 0 so a long compound can wrap: a flex item defaults
            to min-width:auto and refuses to shrink below its longest word,
            which pushed "Ehrenamtsbescheinigung?" 7px out of the button. */}
        <span style={{ fontSize: "16px", minWidth: 0 }}>{question}</span>
        <span style={{ fontSize: "18px", color: open ? "var(--color-gold)" : "rgba(254,250,239,0.5)", flex: "none" }}>{open ? "−" : "+"}</span>
      </button>
      {open && (
        <>
          {answer.split("\n\n").map((paragraph, i) => (
            <p key={i} style={{ fontSize: "16px", lineHeight: 1.7, color: "var(--color-cream-soft)", margin: i === 0 ? "14px 0 0" : "10px 0 0" }}>
              {paragraph}
            </p>
          ))}
          {links?.length > 0 && (
            <div style={{ display: "flex", gap: "16px", flexWrap: "wrap", marginTop: "10px" }}>
              {links.map((link) =>
                link.href.startsWith("/") ? (
                  <Link key={link.href} to={link.href} style={{ fontSize: "14px", color: "var(--color-gold)" }}>
                    {link.label}
                  </Link>
                ) : (
                  <a key={link.href} href={link.href} target="_blank" rel="noopener noreferrer" style={{ fontSize: "14px", color: "var(--color-gold)" }}>
                    {link.label}
                  </a>
                )
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/** Renders a list of FaqItems. Pass `categories` (an array of
 * `{ category, items }`, items being `{ question, answer }`) for a grouped
 * FAQ page (category label above each group) — or `items` directly
 * (`{ question, answer }[]`) for a flat, uncategorised list, e.g. an
 * `embeddedFaq` block at the end of a prose article. */
export default function FaqAccordion({ items, categories }) {
  if (categories?.length > 0) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "32px" }}>
        {categories.map(({ category, items: categoryItems }) => (
          <div key={category} style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <span style={{ fontFamily: "var(--font-serif)", fontStyle: "italic", fontSize: "17px", color: "var(--color-gold)" }}>
              {category}
            </span>
            {categoryItems.map(({ question, answer, links }) => (
              <FaqItem key={question} question={question} answer={answer} links={links} />
            ))}
          </div>
        ))}
      </div>
    );
  }

  if (!items?.length) return null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      {items.map(({ question, answer, links }) => (
        <FaqItem key={question} question={question} answer={answer} links={links} />
      ))}
    </div>
  );
}

/** Groups a flat `{ question, answer, category }[]` (e.g. straight from
 * frontmatter `qa`) into the `{ category, items }[]` shape `FaqAccordion`
 * expects, preserving each category's first-appearance order. */
export function groupFaqByCategory(qa) {
  const order = [];
  const byCategory = new Map();
  for (const { category, ...item } of qa) {
    if (!byCategory.has(category)) {
      byCategory.set(category, []);
      order.push(category);
    }
    byCategory.get(category).push(item);
  }
  return order.map((category) => ({ category, items: byCategory.get(category) }));
}
